import React from 'react';
import { Gutter, Layout, ParagraphHeader, Well } from '@axiom/ui';
import type {
  Candidate,
  CandidateOpportunity,
  Opportunity,
  OpportunityCandidate,
  Taxonomy,
} from '@axiom/validation';

import { OpportunityTalentMatchSummaryChecklistItem } from './OpportunityTalentMatchSummaryChecklistItem';
import type { MatchChecklistItem } from './OpportunityTalentMatchSummaryChecklistUtil';
import { OpportunityTalentMatchSummaryChecklistUtil } from './OpportunityTalentMatchSummaryChecklistUtil';

export type OpportunityTalentMatchSummaryChecklistProps = {
  name?: string;
  candidate: Candidate;
  opportunity: Opportunity;
  opportunityCandidate?: OpportunityCandidate;
  candidateOpportunities: CandidateOpportunity[];
  taxonomy: Taxonomy;
};

export const OpportunityTalentMatchSummaryChecklist = ({
  name = 'OPPORTUNITY_TALENT_MATCH_SUMMARY_CHECKLIST',
  candidate,
  opportunity,
  opportunityCandidate,
  candidateOpportunities,
  taxonomy,
}: OpportunityTalentMatchSummaryChecklistProps) => {
  const checklistItems: MatchChecklistItem[] = [
    OpportunityTalentMatchSummaryChecklistUtil.getAvailabilityHoursChecklistItem(
      {
        candidate,
        opportunity,
        opportunityCandidate,
      }
    ),
    OpportunityTalentMatchSummaryChecklistUtil.getOpenToLowerEscItem({
      opportunityCandidate,
    }),
    OpportunityTalentMatchSummaryChecklistUtil.getCrossFulfillmentChecklistItem(
      {
        candidate,
        candidateOpportunities,
        opportunity,
      }
    ),
    OpportunityTalentMatchSummaryChecklistUtil.getEngagedHoursChecklistItem({
      candidate,
      opportunity,
      candidateOpportunities,
    }),
    OpportunityTalentMatchSummaryChecklistUtil.getCountryChecklistItem({
      candidate,
      opportunity,
    }),
    OpportunityTalentMatchSummaryChecklistUtil.getBarredToWorkOnsiteChecklistItem(
      {
        candidate,
        opportunity,
        opportunityCandidate,
      }
    ),
    OpportunityTalentMatchSummaryChecklistUtil.getBarredToWorkRemotelyChecklistItem(
      {
        candidate,
        opportunity,
        opportunityCandidate,
      }
    ),
    OpportunityTalentMatchSummaryChecklistUtil.getStateChecklistItem({
      candidate,
      opportunity,
    }),
    OpportunityTalentMatchSummaryChecklistUtil.getPracticeAreaChecklistItem({
      candidate,
      opportunity,
      practiceAreas: taxonomy.practiceAreas,
    }),
    OpportunityTalentMatchSummaryChecklistUtil.getIndustryChecklistItem({
      candidate,
      opportunity,
      industries: taxonomy.industries,
    }),
  ]
    .filter((item): item is MatchChecklistItem => !!item)
    .sort((a, b) => +(a.isMatched ?? 0) - +(b.isMatched ?? 0));

  return (
    <Well name={name}>
      <Layout name="checklist" direction="vertical" verticalGutter="4px">
        <Gutter bottom="8px">
          <ParagraphHeader name="CHECKLIST_HEADER">
            Match Checklist
          </ParagraphHeader>
        </Gutter>

        {checklistItems.length > 0 &&
          checklistItems.map(({ id, isMatched, children }) => (
            <OpportunityTalentMatchSummaryChecklistItem
              key={id}
              id={id}
              isMatched={isMatched}
            >
              {children}
            </OpportunityTalentMatchSummaryChecklistItem>
          ))}
      </Layout>
    </Well>
  );
};
